/**
 * Reusable PDF Print Report Component
 * 
 * Generates professional PDF reports with printing functionality
 * 
 * @param {Object} props
 * @param {string} props.reportType - Type of report ('doctor', 'medicalAid', 'script', 'hourly', 'healthnet', or 'scriptTotals')
 * @param {string} props.reportTitle - Report title (e.g., "DOCTOR STATISTICS REPORT")
 * @param {Object} props.branchInfo - Branch information object
 * @param {Array} props.headers - Array of column headers
 * @param {Array} props.data - Array of data rows
 * @param {Object} props.dateRange - Date range object { fromDate, toDate }
 * @param {boolean} props.isPrint - Whether to show print dialog (true) or download (false)
 */

const PrintReport = async ({
  reportType,
  reportTitle,
  branchInfo,
  headers,
  data,
  dateRange,
  isPrint = true
}) => {
  const pad2 = (n) => String(n).padStart(2, '0');

  const formatDateDMY = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  };

  const formatDateTimeDMY = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return `${formatDateDMY(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  const formatDateDMYForFilename = (date) => {
    const dmy = formatDateDMY(date);
    return dmy ? dmy.replaceAll('/', '-') : '';
  };

  // Import jsPDF and autoTable plugin
  const [{ jsPDF: JsPDF }, autoTable] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ]);

  // Determine document orientation based on report type
  const getOrientation = (reportType) => {
    switch (reportType) {
      // All reports use portrait for consistency
      default:
        return 'portrait';
    }
  };

  const orientation = getOrientation(reportType);
  
  // Create new PDF document with appropriate orientation
  // jsPDF expects: orientation, unit, format
  const doc = new JsPDF(orientation, 'pt', 'a4');

  // Set up professional fonts and colors
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(50, 50, 50);

  // Add pharmacy name (centered) - using branch name from system
  const pharmacyName = branchInfo?.branchname || 'DURBAN NORTH PHARMACY';
  const pharmacyNameWidth = doc.getTextWidth(pharmacyName);
  const centerX = (doc.internal.pageSize.width - pharmacyNameWidth) / 2;
  doc.text(pharmacyName, centerX, 20);

  // Add pharmacy address (centered) - using branch address from system
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const pharmacyAddress = branchInfo?.address || '123 Umhlanga Rocks Drive, Durban North, 4051';
  const addressWidth = doc.getTextWidth(pharmacyAddress);
  const addressX = (doc.internal.pageSize.width - addressWidth) / 2;
  doc.text(pharmacyAddress, addressX, 35);

  // Add contact info (centered) - using branch phone from system
  const contactInfo = `Tel: ${branchInfo?.telephoneno || '(031) 123-4567'}`;
  const contactWidth = doc.getTextWidth(contactInfo);
  const contactX = (doc.internal.pageSize.width - contactWidth) / 2;
  doc.text(contactInfo, contactX, 53);

  // Add separator line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(20, 65, doc.internal.pageSize.width - 20, 65);

  // Add report title (centered)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(50, 50, 50);
  const titleWidth = doc.getTextWidth(reportTitle);
  const titleX = (doc.internal.pageSize.width - titleWidth) / 2;
  doc.text(reportTitle, titleX, 80);

  // Add date filter information (centered)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);

  let dateText = '';
  if (dateRange?.fromDate || dateRange?.toDate) {
    const fromStr = dateRange?.fromDate ? formatDateDMY(dateRange.fromDate) : 'All time';
    const toStr = dateRange?.toDate ? formatDateDMY(dateRange.toDate) : 'All time';
    dateText = `Date Range: ${fromStr} to ${toStr}`;
  } else {
    dateText = 'All time data';
  }
  const dateWidth = doc.getTextWidth(dateText);
  const dateX = (doc.internal.pageSize.width - dateWidth) / 2;
  doc.text(dateText, dateX, 110);

  // Prepare table data based on report type
  let tableData;
  let columnStyles;

  if (reportType === 'doctor') {
    tableData = data.map(row => [
      row.doctor,
      row.scripts.toString(),
      `R${parseFloat(row.gross).toFixed(2)}`,
      `R${parseFloat(row.disc).toFixed(2)}`,
      'R0.00',
      `R${parseFloat(row.income).toFixed(2)}`
    ]);

    // Add totals row for doctor stats
    const totalsRow = [
      'TOTALS',
      data.reduce((sum, row) => sum + Number(row.scripts), 0).toString(),
      `R${data.reduce((sum, row) => sum + Number(row.gross), 0).toFixed(2)}`,
      `R${data.reduce((sum, row) => sum + Number(row.disc), 0).toFixed(2)}`,
      'R0.00',
      `R${data.reduce((sum, row) => sum + Number(row.income), 0).toFixed(2)}`
    ];
    tableData.push(totalsRow);

    columnStyles = {
      0: { cellWidth: 45, halign: 'left', fontSize: 7 }, // Doctor
      1: { cellWidth: 20, halign: 'center', fontSize: 7 }, // Scripts
      2: { cellWidth: 30, halign: 'right', fontSize: 7 }, // Gross
      3: { cellWidth: 30, halign: 'right', fontSize: 7 }, // Discount
      4: { cellWidth: 30, halign: 'right', fontSize: 7 }, // Admin
      5: { cellWidth: 30, halign: 'right', fontSize: 7 }  // Income
    };
  } else if (reportType === 'medicalAid') {
    tableData = data.map(row => [
      '', // First column (Medical Aid) should be blank
      row.medAidName,
      row.scripts.toString(),
      `R${parseFloat(row.gross).toFixed(2)}`,
      `R${parseFloat(row.income).toFixed(2)}`,
      `R${parseFloat(row.cost).toFixed(2)}`,
      `R${parseFloat(row.aveIncome).toFixed(2)}`,
      `${row.percentCount}`
    ]);

    columnStyles = {
      0: { cellWidth: 50, halign: 'left', fontSize: 9 },    // Medical Aid (blank)
      1: { cellWidth: 80, halign: 'left', fontSize: 9 },    // Medical Aid Name
      2: { cellWidth: 60, halign: 'right', fontSize: 9 },   // No. of Scripts
      3: { cellWidth: 70, halign: 'right', fontSize: 9 },   // Gross
      4: { cellWidth: 70, halign: 'right', fontSize: 9 },   // Income
      5: { cellWidth: 70, halign: 'right', fontSize: 9 },   // Cost
      6: { cellWidth: 75, halign: 'right', fontSize: 9 },   // Ave.Income
      7: { cellWidth: 90, halign: 'right', fontSize: 9 }    // %Count
    };
  } else if (reportType === 'script') {
    // Handle both summary and detailed script data
    const isDetailData = data.length > 0 && data[0].scriptNumber && data[0].patientName;
    const detailRowsForTotals = isDetailData
      ? data.filter((row) => row.scriptNumber !== 'TOTALS')
      : data;
    const summaryRowsForTotals = !isDetailData
      ? data.filter((row) => row.scriptStatistics !== 'TOTALS')
      : data;
    
    if (isDetailData) {
      // Detailed script records
      tableData = data.map(row => [
        row.scriptNumber,
        row.patientName,
        `R${parseFloat(row.grossIncome).toFixed(2)}`,
        `R${parseFloat((row.discount ?? row.disc) ?? 0).toFixed(2)}`,
        `R${parseFloat(row.admin).toFixed(2)}`,
        `R${parseFloat(row.netIncome).toFixed(2)}`,
        `R${parseFloat(row.member).toFixed(2)}`,
        `R${parseFloat(row.claim).toFixed(2)}`,
        `R${parseFloat(row.dispFee).toFixed(2)}`,
        `R${parseFloat(row.otherFees).toFixed(2)}`
      ]);

      // Add totals row for detailed script stats
      const hasTotalsRow = data.some((row) => row.scriptNumber === 'TOTALS');
      const totalsRow = [
        'TOTALS',
        '',
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number(row.grossIncome), 0).toFixed(2)}`,
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number((row.discount ?? row.disc) ?? 0), 0).toFixed(2)}`,
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number(row.admin), 0).toFixed(2)}`,
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number(row.netIncome), 0).toFixed(2)}`,
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number(row.member), 0).toFixed(2)}`,
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number(row.claim), 0).toFixed(2)}`,
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number(row.dispFee), 0).toFixed(2)}`,
        `R${detailRowsForTotals.reduce((sum, row) => sum + Number(row.otherFees), 0).toFixed(2)}`
      ];
      if (!hasTotalsRow) {
        tableData.push(totalsRow);
      }

      // Column styles for detailed script data
      columnStyles = {
        0: { cellWidth: 60, halign: 'left', fontSize: 8 },   // Script Number
        1: { cellWidth: 90, halign: 'left', fontSize: 8 },   // Patient Name
        2: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Gross Income
        3: { cellWidth: 45, halign: 'right', fontSize: 8 },  // Discount
        4: { cellWidth: 45, halign: 'right', fontSize: 8 },  // Admin
        5: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Net Income
        6: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Member
        7: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Claim
        8: { cellWidth: 50, halign: 'right', fontSize: 8 },  // Disp Fee
        9: { cellWidth: 45, halign: 'right', fontSize: 8 }   // Other Fees
      };
    } else {
      // Summary script data (original logic)
      tableData = data.map(row => [
        row.scriptStatistics,
        `R${parseFloat(row.grossIncome).toFixed(2)}`,
        `R${parseFloat(row.disc).toFixed(2)}`,
        `R${parseFloat(row.admin).toFixed(2)}`,
        `R${parseFloat(row.netIncome).toFixed(2)}`,
        `R${parseFloat(row.member).toFixed(2)}`,
        `R${parseFloat(row.claim).toFixed(2)}`,
        `R${parseFloat(row.dispFee).toFixed(2)}`,
        `R${parseFloat(row.otherFees).toFixed(2)}`
      ]);

      // Add totals row for script stats
      const hasTotalsRow = data.some((row) => row.scriptStatistics === 'TOTALS');
      const totalsRow = [
        'TOTALS',
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.grossIncome), 0).toFixed(2)}`,
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.disc), 0).toFixed(2)}`,
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.admin), 0).toFixed(2)}`,
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.netIncome), 0).toFixed(2)}`,
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.member), 0).toFixed(2)}`,
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.claim), 0).toFixed(2)}`,
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.dispFee), 0).toFixed(2)}`,
        `R${summaryRowsForTotals.reduce((sum, row) => sum + Number(row.otherFees), 0).toFixed(2)}`
      ];
      if (!hasTotalsRow) {
        tableData.push(totalsRow);
      }

      // Basic column styles (will be overridden by report-specific config)
      columnStyles = {
        0: { cellWidth: 40, halign: 'left', fontSize: 8 },   // Script Statistics
        1: { cellWidth: 25, halign: 'right', fontSize: 8 },  // Gross Income
        2: { cellWidth: 20, halign: 'right', fontSize: 8 },  // Discount
        3: { cellWidth: 20, halign: 'right', fontSize: 8 },  // Admin
        4: { cellWidth: 25, halign: 'right', fontSize: 8 },  // Net Income
        5: { cellWidth: 25, halign: 'right', fontSize: 8 },  // Member
        6: { cellWidth: 25, halign: 'right', fontSize: 8 },  // Claim
        7: { cellWidth: 20, halign: 'right', fontSize: 8 },  // Disp Fee
        8: { cellWidth: 20, halign: 'right', fontSize: 8 }   // Other Fees
      };
    }
  } else if (reportType === 'healthnet') {
    tableData = data.map(row => {
      // Check if this is a reversal record
      const isReversal = row.payDue === 'Reversal Accepted';
      
      if (isReversal) {
        // For reversal records, show "Reversal Accepted" in all empty financial columns
        return [
          row.scriptDpn,
          row.date,
          row.member,
          row.reference,
          row.authorisation || 'Reversal Accepted', // Authorisation
          row.gross || 'Reversal Accepted', // Gross
          'Reversal Accepted', // Pay Due
          'Reversal Accepted', // Levy
          'Reversal Accepted', // Other
          'Reversal Accepted', // Levy 2
          'Reversal Accepted'  // Med Aid
        ];
      } else {
        // Normal records - format financial values
        const formatHealthnetValue = (value) => {
          if (value === '' || value === null || value === undefined) return '';
          return `R${parseFloat(value).toFixed(2)}`;
        };
        
        return [
          row.scriptDpn,
          row.date,
          row.member,
          row.reference,
          row.authorisation,
          formatHealthnetValue(row.gross),
          formatHealthnetValue(row.payDue),
          formatHealthnetValue(row.levy),
          formatHealthnetValue(row.other),
          formatHealthnetValue(row.levy2),
          row.medAid || ''
        ];
      }
    });

    columnStyles = {
      0: { cellWidth: 25, halign: 'left', fontSize: 6 },   // Script#DPN
      1: { cellWidth: 20, halign: 'center', fontSize: 6 }, // Date
      2: { cellWidth: 30, halign: 'left', fontSize: 6 },   // Member
      3: { cellWidth: 25, halign: 'left', fontSize: 6 },   // Reference
      4: { cellWidth: 25, halign: 'left', fontSize: 6 },   // Authorisation
      5: { cellWidth: 20, halign: 'right', fontSize: 6 },  // Gross
      6: { cellWidth: 20, halign: 'right', fontSize: 6 },  // Pay Due
      7: { cellWidth: 15, halign: 'right', fontSize: 6 },  // Levy
      8: { cellWidth: 15, halign: 'right', fontSize: 6 },  // Other
      9: { cellWidth: 15, halign: 'right', fontSize: 6 },  // Levy 2
      10: { cellWidth: 20, halign: 'left', fontSize: 6 }   // Med Aid
    };
  } else if (reportType === 'scriptTotals') {
    tableData = data.map(row => [
      row.scriptDpn,
      row.date,
      row.member,
      row.reference,
      row.authorisation,
      `R${parseFloat(row.gross).toFixed(2)}`,
      `R${parseFloat(row.payDue).toFixed(2)}`,
      `R${parseFloat(row.levy).toFixed(2)}`,
      `R${parseFloat(row.other).toFixed(2)}`,
      `R${parseFloat(row.levy2).toFixed(2)}`,
      row.medAid
    ]);

    columnStyles = {
      0: { cellWidth: 25, halign: 'left', fontSize: 6 },   // Script#DPN
      1: { cellWidth: 20, halign: 'center', fontSize: 6 }, // Date
      2: { cellWidth: 30, halign: 'left', fontSize: 6 },   // Member
      3: { cellWidth: 25, halign: 'left', fontSize: 6 },   // Reference
      4: { cellWidth: 25, halign: 'left', fontSize: 6 },   // Authorisation
      5: { cellWidth: 20, halign: 'right', fontSize: 6 },  // Gross
      6: { cellWidth: 20, halign: 'right', fontSize: 6 },  // Pay Due
      7: { cellWidth: 15, halign: 'right', fontSize: 6 },  // Levy
      8: { cellWidth: 15, halign: 'right', fontSize: 6 },  // Other
      9: { cellWidth: 15, halign: 'right', fontSize: 6 },  // Levy 2
      10: { cellWidth: 20, halign: 'left', fontSize: 6 }   // Med Aid
    };
  } else if (reportType === 'hourly') {
    tableData = data.map(row => [
      row.hh,
      `R${parseFloat(row.gross).toFixed(2)}`,
      `R${parseFloat(row.discount).toFixed(2)}`,
      `R${parseFloat(row.admin).toFixed(2)}`,
      `R${parseFloat(row.nett).toFixed(2)}`,
      `R${parseFloat(row.member).toFixed(2)}`,
      `R${parseFloat(row.claim).toFixed(2)}`,
      row.rxItems,
      row.rxItems2,
      `R${parseFloat(row.avg).toFixed(2)}`,
      `R${parseFloat(row.avg2).toFixed(2)}`,
      row.otc,
      row.nd,
      row.mix,
      row.rpt,
      row.rw
    ]);

    }

  // Add table with soft pastel purple header
  const tableConfig = {
    head: [headers],
    body: tableData,
    startY: 65, // Will be overridden by report-specific config
    headStyles: {
      fillColor: [221, 214, 254], // Soft pastel purple (#DDD6FE)
      textColor: [0, 0, 0], // Black text for headers
      fontSize: 10,
      fontStyle: 'bold',
      lineWidth: 0.1,
      font: 'helvetica'
    },
    bodyStyles: {
      textColor: [50, 50, 50],
      fontSize: 9,
      font: 'helvetica'
    },
    alternateRowStyles: {
      fillColor: [248, 248, 252] // Very light purple for alternate rows
    },
    footStyles: {
      fillColor: [230, 230, 250], // Light purple for totals row
      textColor: [50, 50, 50],
      fontSize: 10,
      fontStyle: 'bold'
    },
    styles: {
      cellPadding: 4,
      lineColor: [180, 180, 180],
      lineWidth: 0.1,
      font: 'helvetica'
    },
    margin: { top: 65, left: 15, right: 15 },
    columnStyles
  };

  // Apply report-specific configurations
  switch (reportType) {
    case 'script':
      // Script stats - detect if detailed or summary data
      const isDetailData = data.length > 0 && data[0].scriptNumber && data[0].patientName;
      
      if (isDetailData) {
        // Detailed script records configuration
        tableConfig.startY = 130;
        tableConfig.headStyles.fontSize = 9;
        tableConfig.bodyStyles.fontSize = 8;
        tableConfig.footStyles.fontSize = 9;
        tableConfig.styles.cellPadding = 3;
        tableConfig.margin = { left: 5, right: 5 };
        
        // Column widths for detailed script data (10 columns)
        tableConfig.columnStyles = {
          0: { cellWidth: 60, halign: 'left', fontSize: 8 },   // Script Number
          1: { cellWidth: 90, halign: 'left', fontSize: 8 },   // Patient Name
          2: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Gross Income
          3: { cellWidth: 45, halign: 'right', fontSize: 8 },  // Discount
          4: { cellWidth: 45, halign: 'right', fontSize: 8 },  // Admin
          5: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Net Income
          6: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Member
          7: { cellWidth: 60, halign: 'right', fontSize: 8 },  // Claim
          8: { cellWidth: 50, halign: 'right', fontSize: 8 },  // Disp Fee
          9: { cellWidth: 45, halign: 'right', fontSize: 8 }   // Other Fees
        };
        
        // Set table width to exactly 575pt
        tableConfig.tableWidth = 575;
      } else {
        // Summary script data configuration (original)
        tableConfig.startY = 130;
        tableConfig.headStyles.fontSize = 10;
        tableConfig.bodyStyles.fontSize = 9;
        tableConfig.footStyles.fontSize = 10;
        tableConfig.styles.cellPadding = 3;
        tableConfig.margin = { left: 5, right: 5 };
        
        // Column widths to total exactly 570pt
        tableConfig.columnStyles = {
          0: { cellWidth: 90, halign: 'left', fontSize: 9 },   // Script Statistics
          1: { cellWidth: 70, halign: 'right', fontSize: 9 },  // Gross Income
          2: { cellWidth: 50, halign: 'right', fontSize: 9 },  // Discount
          3: { cellWidth: 50, halign: 'right', fontSize: 9 },  // Admin
          4: { cellWidth: 70, halign: 'right', fontSize: 9 },  // Net Income
          5: { cellWidth: 70, halign: 'right', fontSize: 9 },  // Member
          6: { cellWidth: 70, halign: 'right', fontSize: 9 },  // Claim
          7: { cellWidth: 50, halign: 'right', fontSize: 9 },  // Disp Fee
          8: { cellWidth: 50, halign: 'right', fontSize: 9 }   // Other Fees
        };
        
        // Set table width to exactly 575pt
        tableConfig.tableWidth = 575;
      }
      
      // Enhanced spacing for larger fonts
      tableConfig.styles.overflow = 'linebreak';
      tableConfig.styles.minCellHeight = 16;
      tableConfig.styles.valign = 'middle';
      tableConfig.styles.fontSize = isDetailData ? 8 : 9;
      break;

    case 'doctor':
      // Doctor stats - same styling as script stats
      tableConfig.startY = 130;
      tableConfig.headStyles.fontSize = 10;
      tableConfig.bodyStyles.fontSize = 9;
      tableConfig.footStyles.fontSize = 10;
      tableConfig.styles.cellPadding = 3;
      tableConfig.margin = { left: 5, right: 5 };
      
      // Column widths for doctor stats (6 columns)
      tableConfig.columnStyles = {
        0: { cellWidth: 120, halign: 'left', fontSize: 9 },   // Doctor Name
        1: { cellWidth: 80, halign: 'right', fontSize: 9 },   // Scripts
        2: { cellWidth: 90, halign: 'right', fontSize: 9 },   // Gross
        3: { cellWidth: 90, halign: 'right', fontSize: 9 },   // Income
        4: { cellWidth: 90, halign: 'right', fontSize: 9 },   // Cost
        5: { cellWidth: 95, halign: 'right', fontSize: 9 }    // Ave.Income
      };
      
      tableConfig.tableWidth = 575;
      tableConfig.styles.overflow = 'linebreak';
      tableConfig.styles.minCellHeight = 16;
      tableConfig.styles.valign = 'middle';
      tableConfig.styles.fontSize = 9;
      break;

    case 'medicalAid':
      // Medical aid stats - same styling as script stats
      tableConfig.startY = 130;
      tableConfig.headStyles.fontSize = 10;
      tableConfig.bodyStyles.fontSize = 9;
      tableConfig.footStyles.fontSize = 10;
      tableConfig.styles.cellPadding = 3;
      tableConfig.margin = { left: 5, right: 5 };
      
      // Column widths for medical aid stats (8 columns)
      tableConfig.columnStyles = {
        0: { cellWidth: 50, halign: 'left', fontSize: 9 },    // Medical Aid
        1: { cellWidth: 80, halign: 'left', fontSize: 9 },    // Medical Aid Name
        2: { cellWidth: 60, halign: 'right', fontSize: 9 },   // No. of Scripts
        3: { cellWidth: 70, halign: 'right', fontSize: 9 },   // Gross
        4: { cellWidth: 70, halign: 'right', fontSize: 9 },   // Income
        5: { cellWidth: 70, halign: 'right', fontSize: 9 },   // Cost
        6: { cellWidth: 75, halign: 'right', fontSize: 9 },   // Ave.Income
        7: { cellWidth: 90, halign: 'right', fontSize: 9 }    // %Count
      };
      
      tableConfig.tableWidth = 575;
      tableConfig.styles.overflow = 'linebreak';
      tableConfig.styles.minCellHeight = 16;
      tableConfig.styles.valign = 'middle';
      tableConfig.styles.fontSize = 9;
      break;

    case 'hourly':
      // Hourly distribution - portrait optimized with 575pt width compliance
      tableConfig.startY = 130;
      tableConfig.headStyles.fontSize = 10;
      tableConfig.bodyStyles.fontSize = 9;
      tableConfig.footStyles.fontSize = 10;
      tableConfig.styles.cellPadding = 3;
      tableConfig.margin = { left: 5, right: 5 };
      
      // Column widths that sum to exactly 575pt for maximum width utilization
      tableConfig.columnStyles = {
        0: { cellWidth: 35, halign: 'center', fontSize: 9 },  // HH
        1: { cellWidth: 40, halign: 'right', fontSize: 9 },   // Gross
        2: { cellWidth: 35, halign: 'right', fontSize: 9 },   // Discount
        3: { cellWidth: 35, halign: 'right', fontSize: 9 },   // Admin
        4: { cellWidth: 40, halign: 'right', fontSize: 9 },   // Nett
        5: { cellWidth: 40, halign: 'right', fontSize: 9 },   // Member
        6: { cellWidth: 40, halign: 'right', fontSize: 9 },   // Claim
        7: { cellWidth: 35, halign: 'center', fontSize: 9 },  // #RX Items
        8: { cellWidth: 35, halign: 'center', fontSize: 9 },  // RX Items2
        9: { cellWidth: 40, halign: 'right', fontSize: 9 },   // Avg
        10: { cellWidth: 40, halign: 'right', fontSize: 9 },  // Avg2
        11: { cellWidth: 30, halign: 'center', fontSize: 9 }, // OTC
        12: { cellWidth: 30, halign: 'center', fontSize: 9 }, // N/D
        13: { cellWidth: 30, halign: 'center', fontSize: 9 }, // Mix
        14: { cellWidth: 30, halign: 'center', fontSize: 9 }, // RPT
        15: { cellWidth: 30, halign: 'center', fontSize: 9 }  // Rw
      };
      
      tableConfig.tableWidth = 575;
      tableConfig.styles.overflow = 'linebreak';
      tableConfig.styles.minCellHeight = 16;
      tableConfig.styles.valign = 'middle';
      tableConfig.styles.fontSize = 9;
      
      console.log('Hourly report config:', {
        pageWidth: doc.internal.pageSize.width,
        tableWidth: 575,
        margins: { left: 5, right: 5 },
        totalWidth: 575 + 5 + 5,
        columnWidths: Object.values(tableConfig.columnStyles).reduce((sum, col) => sum + col.cellWidth, 0)
      });
      break;

    case 'healthnet':
    case 'scriptTotals':
      // Healthnet/Script Totals - portrait optimized with exact width compliance
      tableConfig.startY = 130;
      tableConfig.headStyles.fontSize = 10;
      tableConfig.bodyStyles.fontSize = 9;
      tableConfig.footStyles.fontSize = 10;
      tableConfig.styles.cellPadding = 3;
      tableConfig.margin = { left: 5, right: 5 };
      
      // Optimized column widths to use maximum available space (575pt total)
      tableConfig.columnStyles = {
        0: { cellWidth: 55, halign: 'left', fontSize: 9 },   // Script#DPN
        1: { cellWidth: 45, halign: 'center', fontSize: 9 }, // Date
        2: { cellWidth: 65, halign: 'left', fontSize: 9 },   // Member
        3: { cellWidth: 55, halign: 'left', fontSize: 9 },   // Reference
        4: { cellWidth: 55, halign: 'left', fontSize: 9 },   // Authorisation
        5: { cellWidth: 50, halign: 'right', fontSize: 9 },  // Gross
        6: { cellWidth: 50, halign: 'right', fontSize: 9 },  // Pay Due
        7: { cellWidth: 45, halign: 'right', fontSize: 9 },  // Levy
        8: { cellWidth: 45, halign: 'right', fontSize: 9 },  // Other
        9: { cellWidth: 45, halign: 'right', fontSize: 9 },  // Levy 2
        10: { cellWidth: 55, halign: 'left', fontSize: 9 }   // Med Aid
      };
      
      tableConfig.tableWidth = 575;
      tableConfig.styles.overflow = 'linebreak';
      tableConfig.styles.minCellHeight = 16;
      tableConfig.styles.valign = 'middle';
      tableConfig.styles.fontSize = 9;
      
      console.log('Healthnet/ScriptTotals report config:', {
        pageWidth: doc.internal.pageSize.width,
        tableWidth: 575,
        margins: { left: 5, right: 5 },
        totalWidth: 575 + 5 + 5,
        columnWidths: Object.values(tableConfig.columnStyles).reduce((sum, col) => sum + col.cellWidth, 0)
      });
      break;

    default:
      // Default configuration matching script stats styling
      tableConfig.startY = 130;
      tableConfig.headStyles.fontSize = 10;
      tableConfig.bodyStyles.fontSize = 9;
      tableConfig.footStyles.fontSize = 10;
      tableConfig.styles.cellPadding = 3;
      tableConfig.margin = { left: 5, right: 5 };
      tableConfig.tableWidth = 575;
      tableConfig.styles.overflow = 'linebreak';
      tableConfig.styles.minCellHeight = 16;
      tableConfig.styles.valign = 'middle';
      tableConfig.styles.fontSize = 9;
      break;
  }

  autoTable.default(doc, tableConfig);

  // Add footer with timestamp
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on ${formatDateTimeDMY(new Date())} - Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  if (isPrint) {
    // Convert PDF to blob and open print dialog directly
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    // Create hidden iframe to load PDF and trigger print
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    
    // Wait for PDF to load, then trigger print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(pdfUrl);
      }, 1000);
    };
  } else {
    // Generate filename with date range and save the PDF
    const today = formatDateDMYForFilename(new Date());
    const dateRangeStr = (dateRange?.fromDate && dateRange?.toDate) 
      ? `_${formatDateDMYForFilename(dateRange.fromDate)}_to_${formatDateDMYForFilename(dateRange.toDate)}`
      : '';
    const filename = `${reportType === 'doctor' ? 'Doctor_Statistics' : 'Medical_Aid_Statistics'}${dateRangeStr}_${today}.pdf`;
    
    doc.save(filename);
  }
};

export default PrintReport;
